import {
    AfterViewInit,
    Directive,
    DoCheck,
    ElementRef,
    forwardRef,
    HostListener,
    Inject,
    Input,
    KeyValueDiffer,
    KeyValueDiffers,
    OnInit,
    Optional,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { CurrencyMaskConfig, CURRENCY_MASK_CONFIG } from './currencyMask.config';
import { InputHandler } from './input.handler';

export const CURRENCYMASKDIRECTIVE_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CurrencyMaskDirective),
    multi: true,
};

@Directive({
    selector: '[currencyMask]',
    providers: [
        CURRENCYMASKDIRECTIVE_VALUE_ACCESSOR,
        { provide: NG_VALIDATORS, useExisting: CurrencyMaskDirective, multi: true },
    ],
})
export class CurrencyMaskDirective implements AfterViewInit, ControlValueAccessor, DoCheck, OnInit, Validator {
    constructor(
        @Optional() @Inject(CURRENCY_MASK_CONFIG) private currencyMaskConfig: CurrencyMaskConfig,
        private elementRef: ElementRef,
        private keyValueDiffers: KeyValueDiffers,
    ) {
        if (currencyMaskConfig) {
            this.optionsTemplate = { ...this.optionsTemplate, ...currencyMaskConfig };
        }

        this.keyValueDiffer = keyValueDiffers.find({}).create();
    }

    @Input() max: number;
    @Input() min: number;
    @Input() options: any = {};

    inputHandler: InputHandler;
    keyValueDiffer: KeyValueDiffer<any, any>;

    optionsTemplate = {
        align: 'right',
        allowNegative: true,
        decimal: '.',
        precision: 2,
        prefix: '$ ',
        suffix: '',
        thousands: ',',
        allowTextValue: false,
    };

    @HostListener('blur', ['$event'])
    handleBlur(event: any) {
        const onModelTouched = this.inputHandler?.getOnModelTouched();
        if (typeof onModelTouched === 'function') {
            onModelTouched();
        }
    }

    @HostListener('click', ['$event'])
    handleClick(event: any) {
        this.inputHandler.handleClick(event, this.isChromeAndroid());
    }

    @HostListener('cut', ['$event'])
    handleCut(event: any) {
        if (!this.isChromeAndroid()) {
            this.inputHandler.handleCut(event);
        }
    }

    @HostListener('input', ['$event'])
    handleInput(event: any) {
        if (this.isChromeAndroid()) {
            this.inputHandler.handleInput(event);
        }
    }

    @HostListener('keydown', ['$event'])
    handleKeydown(event: any) {
        if (!this.isChromeAndroid()) {
            this.inputHandler.handleKeydown(event);
        }
    }

    @HostListener('keypress', ['$event'])
    handleKeypress(event: any) {
        if (!this.isChromeAndroid()) {
            this.inputHandler.handleKeypress(event);
        }
    }

    @HostListener('keyup', ['$event'])
    handleKeyup(event: any) {
        if (!this.isChromeAndroid()) {
            this.inputHandler.handleKeyup(event);
        }
    }

    @HostListener('paste', ['$event'])
    handlePaste(event: any) {
        if (!this.isChromeAndroid()) {
            this.inputHandler.handlePaste(event);
        }
    }

    ngAfterViewInit() {
        this.elementRef.nativeElement.style.textAlign = this.options.align
            ? this.options.align
            : this.optionsTemplate.align;
    }

    ngDoCheck() {
        if (this.keyValueDiffer.diff(this.options)) {
            this.elementRef.nativeElement.style.textAlign = this.options.align
                ? this.options.align
                : this.optionsTemplate.align;
            this.inputHandler.updateOptions((<any>Object).assign({}, this.optionsTemplate, this.options));
        }
    }

    ngOnInit() {
        this.inputHandler = new InputHandler(
            this.elementRef.nativeElement,
            (<any>Object).assign({}, this.optionsTemplate, this.options),
        );
    }

    isChromeAndroid(): boolean {
        return /chrome/i.test(navigator.userAgent) && /android/i.test(navigator.userAgent);
    }

    registerOnChange(callbackFunction: Function): void {
        this.inputHandler.setOnModelChange(callbackFunction);
    }

    registerOnTouched(callbackFunction: Function): void {
        this.inputHandler.setOnModelTouched(callbackFunction);
    }

    setDisabledState(value: boolean): void {
        this.elementRef.nativeElement.disabled = value;
    }

    validate(abstractControl: AbstractControl): { [key: string]: any } {
        const result: any = {};

        if (abstractControl.value > this.max) {
            result.max = true;
        }

        if (abstractControl.value < this.min) {
            result.min = true;
        }

        return result.max || result.min ? result : null;
    }

    writeValue(value: number): void {
        const allowTextValue = !!(this.options && this.options.allowTextValue);

        if (allowTextValue && typeof value === 'string') {
            const stringValue: string = value;
            const trimmed = stringValue.trim();

            if (trimmed === '') {
                this.inputHandler.setValue(null);
                return;
            }

            const parsed = Number(trimmed);
            if (!Number.isNaN(parsed)) {
                this.inputHandler.setValue(parsed);
                return;
            }

            this.elementRef.nativeElement.value = stringValue;
            return;
        }

        this.inputHandler.setValue(value as any);
    }
}
